import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Switch,
  FormControlLabel,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Button,
  CircularProgress,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Email as EmailIcon,
  Notifications as NotificationsIcon,
  ShoppingCart as ShoppingIcon,
  Chat as ChatIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useTranslation } from '../../../contexts/TranslationContext';
import { UserSettingsService } from '../../../services/userSettingsService';
import toast from 'react-hot-toast';

const NotificationsTab: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [notifications, setNotifications] = useState({
    email: {
      orderUpdates: true,
      promotions: false,
      securityAlerts: true,
      weeklyDigest: true,
    },
    push: {
      newMessages: true,
      orderStatus: true,
      priceDrops: false,
      recommendations: true,
    },
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const countdownRef = useRef<number | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const row = await UserSettingsService.getMySettings();
        if (mounted && row) {
          setNotifications(prev => ({
            email: { ...prev.email, ...(row.email || {}) },
            push: { ...prev.push, ...(row.push || {}) },
          }));
        }
      } catch (e: any) {
        console.error('Load user settings failed', e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      await UserSettingsService.upsertMySettings({
        email: notifications.email,
        push: notifications.push,
      });
      toast.success(t('common.saved'));
    } catch (e: any) {
      console.error('Save user settings failed', e);
      toast.error(t('common.save_failed'));
    } finally {
      setSaving(false);
    }
  };

  const ensurePermission = async (): Promise<boolean> => {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      toast.error(t('settings.notifications.push.unsupported'));
      return false;
    }
    if (Notification.permission === 'denied') {
      toast.error(t('settings.notifications.push.permissionBlocked'));
      return false;
    }
    let finalPermission: NotificationPermission = Notification.permission;
    if (finalPermission === 'default') {
      await Notification.requestPermission();
      finalPermission = Notification.permission;
    }
    if (finalPermission !== 'granted') {
      toast.error(t('settings.notifications.push.permissionDenied'));
      return false;
    }
    return true;
  };

  const handleStartDelayedTest = async () => {
    const ok = await ensurePermission();
    if (!ok) return;
    if (countdownRef.current) window.clearInterval(countdownRef.current);
    setCountdown(5);
    const id = window.setInterval(() => {
      setCountdown((prev) => {
        const next = (prev ?? 0) - 1;
        if (next <= 0) {
          window.clearInterval(id);
          countdownRef.current = null;
          setCountdown(null);
          // fire notification
          try {
            const n = new Notification(t('settings.notifications.push.testTitle'), {
              body: t('settings.notifications.push.testBody'),
              icon: '/favicon.ico',
            });
            setTimeout(() => n.close(), 4000);
            toast.success(t('settings.notifications.push.sent'));
          } catch (e) {
            toast.error(t('settings.notifications.push.permissionDenied'));
          }
        }
        return next;
      });
    }, 1000);
    countdownRef.current = id as unknown as number;
  };

  useEffect(() => {
    return () => {
      if (countdownRef.current) {
        window.clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    };
  }, []);

  const handleToggle = (category: string, setting: string) => {
    setNotifications(prev => {
      const categoryKey = category as keyof typeof prev;
      const settingKey = setting as keyof typeof prev[typeof categoryKey];
      
      return {
        ...prev,
        [categoryKey]: {
          ...prev[categoryKey],
          [settingKey]: !prev[categoryKey][settingKey],
        },
      };
    });
  };

  const handleTestPush = async () => {
    try {
      if (typeof window === 'undefined' || !('Notification' in window)) {
        toast.error(t('settings.notifications.push.unsupported'));
        return;
      }
      // If permission is blocked, inform user
      if (Notification.permission === 'denied') {
        toast.error(t('settings.notifications.push.permissionBlocked'));
        return;
      }
      // Request permission if needed without reassigning a narrowed union
      let finalPermission: NotificationPermission = Notification.permission;
      if (finalPermission === 'default') {
        await Notification.requestPermission();
        finalPermission = Notification.permission;
      }
      if (finalPermission !== 'granted') {
        toast.error(t('settings.notifications.push.permissionDenied'));
        return;
      }
      // Show test notification
      const n = new Notification(t('settings.notifications.push.testTitle'), {
        body: t('settings.notifications.push.testBody'),
        icon: '/favicon.ico'
      });
      // Auto close after a short period to avoid clutter
      setTimeout(() => n.close(), 4000);
      toast.success(t('settings.notifications.push.sent'));
    } catch (e) {
      toast.error(t('settings.notifications.push.permissionDenied'));
    }
  };

  const notificationCategories = [
    {
      key: 'email',
      title: t('settings.notifications.category.email'),
      icon: <EmailIcon />,
      color: '#2196F3',
      settings: [
        { key: 'orderUpdates', label: t('settings.notifications.setting.orderUpdates.label'), description: t('settings.notifications.setting.orderUpdates.desc') },
        { key: 'promotions', label: t('settings.notifications.setting.promotions.label'), description: t('settings.notifications.setting.promotions.desc') },
        { key: 'securityAlerts', label: t('settings.notifications.setting.securityAlerts.label'), description: t('settings.notifications.setting.securityAlerts.desc') },
        { key: 'weeklyDigest', label: t('settings.notifications.setting.weeklyDigest.label'), description: t('settings.notifications.setting.weeklyDigest.desc') },
      ],
    },
    {
      key: 'push',
      title: t('settings.notifications.category.push'),
      icon: <NotificationsIcon />,
      color: '#FF9800',
      settings: [
        { key: 'newMessages', label: t('settings.notifications.setting.newMessages.label'), description: t('settings.notifications.setting.newMessages.desc') },
        { key: 'orderStatus', label: t('settings.notifications.setting.orderStatus.label'), description: t('settings.notifications.setting.orderStatus.desc') },
        { key: 'priceDrops', label: t('settings.notifications.setting.priceDrops.label'), description: t('settings.notifications.setting.priceDrops.desc') },
        { key: 'recommendations', label: t('settings.notifications.setting.recommendations.label'), description: t('settings.notifications.setting.recommendations.desc') },
      ],
    },
  ] as const;

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 240, py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
        <Box sx={{ mb: 4, display: 'flex', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'space-between', gap: 2, flexDirection: isMobile ? 'column' : 'row' }}>
          <Box>
            <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 'bold', mb: 1, color: 'text.primary' }}>
              {t('settings.notifications.title')}
            </Typography>
            <Typography variant={isMobile ? 'body2' : 'body1'} sx={{ color: 'text.secondary' }}>
              {t('settings.notifications.subtitle')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, width: isMobile ? '100%' : 'auto' }}>
            <Button fullWidth={isMobile} size={isMobile ? 'small' : 'medium'} variant="contained" onClick={handleSave} disabled={saving || loading}>
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </Box>
        </Box>

        {notificationCategories.map((category, categoryIndex) => (
          <motion.div
            key={category.key}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: categoryIndex * 0.1 }}
          >
            <Paper
              elevation={0}
              sx={{
                mb: 3,
                borderRadius: 3,
                border: '1px solid rgba(0, 0, 0, 0.05)',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0.6) 100%)',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  p: { xs: 2, md: 3 },
                  background: `linear-gradient(135deg, ${category.color}15 0%, ${category.color}08 100%)`,
                  borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1, flexWrap: 'wrap' }}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      backgroundColor: `${category.color}20`,
                      mr: 2,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {category.icon}
                  </Box>
                  <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ fontWeight: 'bold', color: 'text.primary', flex: 1, minWidth: 180 }}>
                    {category.title}
                  </Typography>
                  {category.key === 'push' && (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', width: isMobile ? '100%' : 'auto', justifyContent: isMobile ? 'flex-end' : 'flex-start', flexWrap: 'wrap' }}>
                      <Button variant="outlined" size={isMobile ? 'small' : 'small'} onClick={handleTestPush} disabled={!!countdown}>
                        {t('settings.notifications.push.testButton')}
                      </Button>
                      <Button variant="outlined" size={isMobile ? 'small' : 'small'} onClick={handleStartDelayedTest} disabled={!!countdown}>
                        {countdown !== null ? `${t('settings.notifications.push.countdownPrefix')} ${countdown}${t('settings.notifications.push.secondsShort')}` : t('settings.notifications.push.testButtonDelayed')}
                      </Button>
                    </Box>
                  )}
                </Box>
              </Box>

              <List sx={{ p: 0 }}>
                {category.settings.map((setting, settingIndex) => {
                  const isEnabled = (notifications as any)[category.key]?.[setting.key] || false;
                  
                  return (
                    <ListItem
                      key={setting.key}
                      sx={{
                        px: { xs: 2, md: 3 },
                        py: { xs: 1.5, md: 2 },
                        borderBottom: settingIndex < category.settings.length - 1 ? '1px solid rgba(0, 0, 0, 0.05)' : 'none',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.02)',
                        },
                        alignItems: 'center',
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: { xs: 28, md: 40 } }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: isEnabled ? category.color : 'text.disabled',
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant={ isMobile ? 'body1' : 'subtitle1' } sx={{ fontWeight: 500 }}>
                              {setting.label}
                            </Typography>
                            {isEnabled && (
                              <Chip
                                label={t('settings.notifications.chip.active')}
                                size="small"
                                sx={{
                                  backgroundColor: `${category.color}20`,
                                  color: category.color,
                                  fontSize: '0.75rem',
                                  height: 20,
                                }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Typography variant={ isMobile ? 'caption' : 'body2' } sx={{ color: 'text.secondary', mt: 0.5 }}>
                            {setting.description}
                          </Typography>
                        }
                      />
                      {isMobile ? (
                        <Box sx={{ ml: 1 }}>
                          <Switch
                            checked={isEnabled}
                            onChange={() => handleToggle(category.key as string, setting.key)}
                            size="small"
                            sx={{
                              '& .MuiSwitch-thumb': {
                                backgroundColor: isEnabled ? category.color : 'text.disabled',
                              },
                              '& .MuiSwitch-track': {
                                backgroundColor: isEnabled ? `${category.color}40` : 'rgba(0, 0, 0, 0.1)',
                              },
                            }}
                          />
                        </Box>
                      ) : (
                        <ListItemSecondaryAction>
                          <Switch
                            checked={isEnabled}
                            onChange={() => handleToggle(category.key as string, setting.key)}
                            sx={{
                              '& .MuiSwitch-thumb': {
                                backgroundColor: isEnabled ? category.color : 'text.disabled',
                              },
                              '& .MuiSwitch-track': {
                                backgroundColor: isEnabled ? `${category.color}40` : 'rgba(0, 0, 0, 0.1)',
                              },
                            }}
                          />
                        </ListItemSecondaryAction>
                      )}
                    </ListItem>
                  );
                })}
              </List>
            </Paper>
          </motion.div>
        ))}

        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            border: '1px solid rgba(0, 0, 0, 0.05)',
            background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.05) 0%, rgba(76, 175, 80, 0.02) 100%)',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: 'text.primary' }}>
            💡 {t('settings.notifications.proTip.title')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('settings.notifications.proTip.text')}
          </Typography>
        </Paper>
          </>
        )}
      </motion.div>
    </Box>
  );
};

export default NotificationsTab;
